import { useState, type FormEvent } from 'react';

import { ApiRequestError, api } from '../lib/api.js';
import { Alert, Button, Card, CardHeader, Field, TextInput } from '../components/ui.js';

/**
 * Primeiro acesso: trocar a senha provisoria.
 *
 * Todas as senhas que este produto gera nascem conhecidas por outra pessoa — a
 * do dono apareceu numa tela da landing, a do porteiro foi ditada no balcao.
 * Enquanto elas valerem, "quem fez a acao" no log de auditoria deixa de
 * identificar alguem e vira um cracha que o grupo inteiro pode ter usado.
 *
 * A tela ocupa o app inteiro em vez de ser um aviso dispensavel, porque o
 * backend recusa toda rota de negocio enquanto o flag estiver de pe: mostrar o
 * menu seria oferecer botoes que respondem 403.
 *
 * Ao trocar, o servidor derruba TODAS as sessoes — inclusive esta. Por isso o
 * fim do fluxo e a tela de login, e nao o painel: alem de ser o certo em
 * seguranca, faz a pessoa provar que a senha nova funciona enquanto ainda
 * lembra dela.
 */
export function ChangePasswordPage({ email }: { email: string }) {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  const tooShort = newPassword.length > 0 && newPassword.length < 8;
  const mismatch = confirmation.length > 0 && confirmation !== newPassword;
  const canSubmit =
    currentPassword.length > 0 && newPassword.length >= 8 && confirmation === newPassword;

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;

    setSending(true);
    setError(null);

    try {
      await api.post<void>('/auth/password', { currentPassword, newPassword });
      // A sessao morreu junto com a troca. Recarregar leva ao login, que e
      // exatamente onde a pessoa precisa estar agora.
      window.location.assign('/');
    } catch (err) {
      setError(
        err instanceof ApiRequestError ? err.message : 'Nao foi possivel trocar a senha.',
      );
      setSending(false);
    }
  }

  return (
    <div className="safe-area-all safe-screen grid place-items-center px-4 py-10">
      <Card className="w-full max-w-md">
        <CardHeader
          title="Defina sua senha"
          subtitle="A senha que voce recebeu foi criada por outra pessoa. Escolha uma so sua para continuar."
        />

        <form className="grid gap-4 px-5 pb-5 sm:px-6" onSubmit={submit}>
          <p className="text-sm text-muted">
            Entrando como <span className="font-semibold text-fg">{email}</span>
          </p>

          {error && <Alert>{error}</Alert>}

          <Field label="Senha atual">
            <TextInput
              type="password"
              value={currentPassword}
              onChange={(event) => setCurrentPassword(event.target.value)}
              autoComplete="current-password"
              placeholder="A que voce recebeu"
            />
          </Field>

          <Field
            label="Nova senha"
            error={tooShort ? 'Use pelo menos 8 caracteres.' : undefined}
          >
            <TextInput
              type="password"
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <Field
            label="Repita a nova senha"
            error={mismatch ? 'As senhas nao conferem.' : undefined}
          >
            <TextInput
              type="password"
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              autoComplete="new-password"
            />
          </Field>

          <Button type="submit" loading={sending} disabled={!canSubmit}>
            Salvar e entrar de novo
          </Button>

          <p className="text-sm text-faint">
            Por seguranca, todas as sessoes abertas com a senha antiga sao encerradas.
          </p>
        </form>
      </Card>
    </div>
  );
}
